/**
 * WalletPage
 * Flutter source: wallet_screen.dart
 *
 * Displays the wallet card header with coins/diamonds balance
 * and a paginated list of transactions below.
 */
import { useCallback } from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { FlexibleHeaderList } from '@/presentation/components/common/appBar/flexibleHeaderList/FlexibleHeaderList';
import type { AppBarOptions } from '@/presentation/components/common/appBar/flexibleHeaderList/FlexibleHeaderList';
import { ExpauseLoadingIndicator } from '@/presentation/components/common/loadingIndicators/ExpauseLoadingIndicator';
import { useTheme } from '@/presentation/contexts/ThemeContext';
import { useLocale } from '@/presentation/contexts/LocaleContext';
import { useResponsiveDesign } from '@/presentation/responsiveDesign/responsiveDesign';
import { useBasePage } from '@/presentation/hooks/useBasePage';
import { Navigator } from '@/presentation/routing/Navigator';
import { InfoSheet } from '@/presentation/components/common/sheet/info/InfoSheet';
import { selectExchangeRate } from '@/presentation/store/slices/payment/paymentSlice';
import { qaAttr } from '@/presentation/utils/qaAttr';
import { AppTransactionType } from '@/domain/enums/transaction/AppTransactionType';
import type { RootState } from '@/presentation/store';
import type { AppTransactionEntity } from '@/domain/entities/transaction/appTransactionEntity';
import type { UserMediaEntity } from '@/domain/entities/userMedia/userMediaEntity';
import { useWallet } from './hooks/useWallet';
import { WalletCard } from './components/WalletCard';
import { TransactionItemRouter } from './components/TransactionItemRouter';

export const WalletPage = () => {
  const { theme } = useTheme();
  const { l10n } = useLocale();
  const responsive = useResponsiveDesign();
  const location = useLocation();

  // Admin variant of the wallet screen. Flutter threads this as a `WalletScreen`
  // constructor arg; on web it travels via router state (set by
  // `Navigator.navigateToWallet({ isFromAdmin })`). When true: admin app bar
  // (title "Transactions", no wallet card) + admin-scoped (all-users) fetch.
  const isFromAdmin = (location.state as { isFromAdmin?: boolean } | null)?.isFromAdmin ?? false;

  const currentUser = useSelector((state: RootState) => state.auth.user);
  const currentUserId = currentUser?.firebaseId ?? '';
  const exchangeRate = useSelector(selectExchangeRate);

  const {
    transactions,
    wallet,
    isLoading,
    hasMore,
    error,
    loadMore,
    initialize,
  } = useWallet(isFromAdmin);

  useBasePage({
    onInit: () => {
      (async () => {
        await initialize();
      })();
    },
  });

  const handleBack = () => {
    Navigator.goBack();
  };

  // Coarse lifecycle token for the wallet root, derived from existing state only.
  const walletStatus = error
    ? 'error'
    : isLoading && transactions.length === 0
      ? 'loading'
      : transactions.length === 0
        ? 'empty'
        : 'ready';

  const showNotImplementedYetSheet = useCallback(() => {
    InfoSheet.showNotImplementedYet({ l10n });
  }, [l10n]);

  const handleContentTap = useCallback((transaction: AppTransactionEntity) => {
    if (!transaction.mediaInfo) return;

    const mediaInfo = transaction.mediaInfo;
    const media: UserMediaEntity = {
      id: mediaInfo.mediaId ?? '',
      type: mediaInfo.type,
      relativePath: mediaInfo.path,
      relativeThumbnailPath: mediaInfo.thumbnail,
      firebaseId: transaction.mediaInfo.firebaseId ?? '',
      createdTimestamp: transaction.createdDate,
      updatedTimestamp: transaction.createdDate,
      userName: transaction.usersInfo.name,
      aspectRatio: mediaInfo.aspectRatio,
      duration: mediaInfo.duration,
      allowUnlock: mediaInfo.allowUnlock,
      allowEarlyAccess: mediaInfo.allowEarlyAccess ?? false,
      isProcessing: false,
      isDraft: false,
      allowComments: true,
      showCoins: false,
      shareLink: '',
      reports: 0,
      blocked: false,
      isStartUp: false,
      isLocal: false,
      isContent: true,
    };

    Navigator.navigateToContentDetails(
      mediaInfo.mediaId ?? '',
      { medias: [media] },
    );
  }, []);

  const renderTransaction = useCallback((transaction: AppTransactionEntity) => {
    // Stable per-row id + type token for the qa-tester. Tagged here once (the
    // single consistent place per the qa convention) rather than in each routed
    // item, reusing the same id composition the list key already derives from.
    const txId = `${transaction.usersInfo.userId}_${transaction.createdDate}`;
    const txType = AppTransactionType[transaction.transactionType] ?? String(transaction.transactionType);

    return (
      <div
        {...qaAttr({ id: `wallet-tx-${txId}`, value: txType })}
        style={{ padding: responsive.spacing.base }}
      >
        <TransactionItemRouter
          transaction={transaction}
          currentUserId={currentUserId}
          onContentTap={handleContentTap}
          isFromAdmin={isFromAdmin}
        />
      </div>
    );
  }, [currentUserId, responsive.spacing.base, handleContentTap, isFromAdmin]);

  // Admin variant: title "Transactions" + no wallet card (no flexibleSpace).
  // Owner variant: title "Wallet" + the parallax wallet-card flexibleSpace.
  // Flutter: wallet_screen.dart:329-330 (_adminAppBar vs _walletAppBar).
  const appBarOptions: AppBarOptions = isFromAdmin
    ? {
        title: l10n.wallet.transactionsTitle,
        onLeadingTap: handleBack,
        backgroundColor: theme.background,
      }
    : {
        title: l10n.wallet.title,
        onLeadingTap: handleBack,
        backgroundColor: theme.background,
        flexibleSpace: {
          parallax: true,
          background: (
            <WalletCard
              wallet={wallet}
              exchangeRate={exchangeRate}
              onActionTap={showNotImplementedYetSheet}
            />
          ),
        },
      };

  return (
    <FlexibleHeaderList
      qa={{ id: isFromAdmin ? 'wallet-admin' : 'wallet', status: walletStatus }}
      listQa={{ id: 'wallet-list' }}
      items={transactions}
      renderItem={renderTransaction}
      getItemKey={(transaction, index) => `${transaction.usersInfo.userId}_${transaction.createdDate}_${index}`}
      onLoadMore={loadMore}
      hasMore={hasMore}
      isLoadingMore={isLoading && transactions.length > 0}
      errorOnFirstFetchComponent={
        error ? (
          <div className="text-center" style={{ padding: `${responsive.spacing['3xl']}px 0` }}>
            <p style={{ color: theme.text.secondary }}>{error}</p>
          </div>
        ) : undefined
      }
      emptyListComponent={
        isLoading ? (
          <div className="flex justify-center" style={{ padding: `${responsive.spacing['3xl']}px 0` }}>
            <ExpauseLoadingIndicator />
          </div>
        ) : (
          <div className="text-center" style={{ padding: `${responsive.spacing['3xl']}px 0` }}>
            <p style={{ color: theme.text.secondary }}>
              {l10n.wallet.noTransactions}
            </p>
          </div>
        )
      }
      backgroundColor={theme.background}
      appBarOptions={appBarOptions}
    />
  );
};
